import { Handle, Position } from "@xyflow/react";
import { Box, Typography, useTheme, Chip } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { ValueChainNodeData } from "src/utils/valueChainUtils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface ValueChainNodeProps {
    data: ValueChainNodeData & {
        onToggleCollapse?: (nodeId: string) => void;
        onNodeClick?: (entity: ValueChainNodeData["entity"]) => void;
        layoutDirection?: "TB" | "LR";
    };
    id: string;
    selected?: boolean;
}

export const ValueChainNode = ({ data, id, selected }: ValueChainNodeProps) => {
    const theme = useTheme();
    const { entity, isCollapsed, childrenCount, layoutDirection = "TB" } = data;

    // Determine handle positions based on layout direction
    const sourcePosition = layoutDirection === "LR" ? Position.Right : Position.Bottom;
    const targetPosition = layoutDirection === "LR" ? Position.Left : Position.Top;

    const getTypeIcon = () => {
        switch (entity.type) {
            case "ingredient":
                return "ingredients";
            case "intermediate_product":
                return "products";
            case "product":
                return "products";
            default:
                return "informations";
        }
    };

    const getTypeLabel = () => {
        switch (entity.type) {
            case "ingredient":
                return "Ingrédient";
            case "intermediate_product":
                return "Produit intermédiaire";
            case "product":
                return entity.isIntermediate ? "Produit intermédiaire" : "Produit";
            default:
                return "Entité";
        }
    };

    const getTypeColor = () => {
        switch (entity.type) {
            case "ingredient":
                return theme.palette.success.main;
            case "intermediate_product":
                return theme.palette.primary.main;
            case "product":
                return entity.isIntermediate ? theme.palette.primary.main : theme.palette.secondary.main;
            default:
                return theme.palette.grey[500];
        }
    };

    return (
        <Box
            onClick={() => data.onNodeClick?.(entity)}
            sx={{
                width: 220,
                minHeight: 100,
                borderRadius: 2,
                border: `2px solid ${selected ? theme.palette.primary.main : getTypeColor()}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow: selected ? 4 : 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                    boxShadow: 3,
                    borderColor: theme.palette.primary.main,
                },
            }}
        >
            <Handle type="target" position={targetPosition} />

            {/* Header */}
            <Box
                sx={{
                    p: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: `${getTypeColor()}15`,
                }}
            >
                <FoodsIcon icon={getTypeIcon()} size={2} />
                <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 600, flex: 1 }}>
                    {getTypeLabel()}
                </Typography>
                {childrenCount > 0 && (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            data.onToggleCollapse?.(id);
                        }}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            "&:hover": {
                                opacity: 0.7,
                            },
                        }}
                    >
                        {isCollapsed ?
                            <ChevronRightIcon fontSize="small" />
                        :   <ExpandMoreIcon fontSize="small" />}
                    </Box>
                )}
            </Box>

            {/* Content */}
            <Box sx={{ p: 1.5 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        mb: 0.5,
                        fontSize: "0.85rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                    }}
                >
                    {entity.title}
                </Typography>

                {entity.ownedByName && (
                    <Chip
                        label={entity.ownedByName}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            mt: 0.5,
                            backgroundColor: `${theme.palette.info.main}15`,
                            color: theme.palette.info.main,
                        }}
                    />
                )}

                {entity.quantity !== undefined && entity.quantity !== null && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            mt: 0.5,
                            color: theme.palette.text.secondary,
                            fontSize: "0.7rem",
                        }}
                    >
                        {entity.quantity} kg
                    </Typography>
                )}
            </Box>

            <Handle type="source" position={sourcePosition} />
        </Box>
    );
};
